import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Filter, X, SlidersHorizontal, Check, ChevronDown, Clock, Globe, ExternalLink, Play } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Breadcrumb } from '../components/ui/Breadcrumb';
import { CourseCard } from '../components/common/CourseCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { API_BASE } from '../config/api';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { useTenant } from '../contexts/TenantContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
// CompetencyFilter removed per user request
import AddToPathwayButton from '../components/common/AddToPathwayButton';

const API = `${API_BASE}/api`;

// External Course Card Component - Uses platform gold color palette
function ExternalCourseCard({ course }) {
  const navigate = useNavigate();

  const handleViewCourse = () => {
    navigate(course.module_id ? `/external-learning/${course.module_id}` : `/external-courses/${course.course_id}`);
  };

  const getLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'introductory': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'intermediate': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'advanced': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  return (
    <div 
      className="bg-navigate-surface border border-zinc-700/50 rounded-xl overflow-hidden hover:border-navigate-gold/50 transition-all group cursor-pointer"
      onClick={handleViewCourse}
      data-testid={`external-course-card-${course.course_id || course.module_id}`}
    >
      {course.thumbnail_url ? (
        <div className="aspect-video overflow-hidden bg-zinc-900">
          <img src={course.thumbnail_url.startsWith('/api/') ? `${API_BASE}${course.thumbnail_url}` : course.thumbnail_url} alt=""
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-testid={`external-course-thumb-${course.course_id}`} />
        </div>
      ) : (
        <div className="h-2 bg-gradient-to-r from-navigate-gold/40 to-amber-600/40" />
      )}
      
      {/* Content */}
      <div className="p-5">
        {/* Badges Row */}
        <div className="flex items-center gap-2 mb-3">
          <Badge variant="outline" className="bg-zinc-800/50 text-zinc-300 border-zinc-600 text-xs">
            <Globe className="h-3 w-3 mr-1" />
            External
          </Badge>
          {course.level && (
            <Badge className={`text-xs ${getLevelColor(course.level)}`}>
              {course.level}
            </Badge>
          )}
        </div>

        {/* Specialisation */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-navigate-gold font-medium">{course.specialisation}</span>
          {course.original_category && course.original_category !== course.specialisation && (
            <>
              <span className="text-zinc-600">•</span>
              <span className="text-xs text-zinc-500">{course.original_category}</span>
            </>
          )}
        </div>
        
        <h3 className="font-semibold text-white mb-2 line-clamp-2 group-hover:text-navigate-gold transition-colors">
          {course.title}
        </h3>
        
        <p className="text-sm text-zinc-400 line-clamp-2 mb-4">
          {course.description}
        </p>
        
        <div className="flex items-center gap-4 text-sm text-zinc-500 mb-4">
          {course.estimated_duration && (
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {course.estimated_duration}
            </span>
          )}
          {course.provider && (
            <span className="flex items-center gap-1">
              <Globe className="h-3.5 w-3.5" />
              {course.provider}
            </span>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleViewCourse();
            }}
            className="flex-1 bg-navigate-gold hover:bg-amber-500 text-black px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Play className="h-4 w-4" />
            View Course
          </button>
        </div>
      </div>
    </div>
  );
}

// Using the same specialisations as the AI Course Generator
const SPECIALISATIONS = [
  "Anti-Corruption",
  "Anti-Financial Crime",
  "Anti-Money Laundering",
  "Artificial Intelligence",
  "Compliance",
  "Conduct",
  "Cybersecurity",
  "Data Privacy",
  "Fraud",
  "GRC",
  "IT Governance",
  "Operations",
  "Risk Management",
  "Social Impact Compliance"
];

const LEVELS = [
  { value: 'all', label: 'All Levels' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' }
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'price-low', label: 'Price: Low to High' },
  { value: 'price-high', label: 'Price: High to Low' },
  { value: 'cpd', label: 'Most CPD Points' }
];

export default function CourseCatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenant } = useTenant();
  const [courses, setCourses] = useState([]);
  const [externalCourses, setExternalCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showSpecDropdown, setShowSpecDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'external' ? 'external' : 'internal'); // 'internal' or 'external'
  const [externalSpecialisations, setExternalSpecialisations] = useState([]);
  const [selectedExternalSpec, setSelectedExternalSpec] = useState('all');
  const [pathwayItemIds, setPathwayItemIds] = useState(new Set()); // Track items already in pathway

  // Check if external courses feature is enabled for this tenant
  const { isFeatureEnabled } = useFeatureFlags();
  const showExternalCourses = isFeatureEnabled('external_courses');

  // Filter states
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [level, setLevel] = useState(searchParams.get('level') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'newest');
  const [selectedSpecialisations, setSelectedSpecialisations] = useState([]);
  const [selectedCompetencyIds, setSelectedCompetencyIds] = useState([]);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Get auth token
  const getAuthToken = () => {
    return localStorage.getItem('token');
  };

  // Load user's competency pathway items on mount (for hydration)
  useEffect(() => {
    const loadPathwayItems = async () => {
      const token = getAuthToken();
      if (!token) return;

      try {
        const res = await axios.get(`${API}/competencies/my-learning-pathway`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Extract item_ids of type 'course' into a Set for O(1) lookup
        const courseIds = new Set(
          (res.data?.items || [])
            .filter(item => item.item_type === 'course')
            .map(item => item.item_id)
        );
        setPathwayItemIds(courseIds);
      } catch (error) {
        console.log('Could not load pathway items:', error.message);
      }
    };

    loadPathwayItems();
  }, []);

  // Load user preferences on mount
  useEffect(() => {
    const loadUserPreferences = async () => {
      const token = getAuthToken();
      if (!token) {
        setPreferencesLoaded(true);
        return;
      }

      try {
        const res = await axios.get(`${API}/user/preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.data?.filter_preferences?.specialisations) {
          setSelectedSpecialisations(res.data.filter_preferences.specialisations);
        }
      } catch (error) {
        // Preferences endpoint may not exist yet or user not logged in
        console.log('Could not load preferences:', error.message);
      } finally {
        setPreferencesLoaded(true);
      }
    };

    loadUserPreferences();
  }, []);

  // Track and save preferences when specialisations change
  const savePreferences = useCallback(async (specs) => {
    const token = getAuthToken();
    if (!token || !preferencesLoaded) return;

    try {
      // Track behavior event
      await axios.post(`${API}/user/track-behavior`, {
        event_type: 'filter_preference',
        data: {
          filter_type: 'specialisation',
          selected_values: specs
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Save preferences
      await axios.put(`${API}/user/preferences`, {
        filter_preferences: {
          specialisations: specs
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.log('Could not save preferences:', error.message);
    }
  }, [preferencesLoaded]);

  // Toggle specialisation selection
  const toggleSpecialisation = (spec) => {
    setSelectedSpecialisations(prev => {
      const newSelection = prev.includes(spec)
        ? prev.filter(s => s !== spec)
        : [...prev, spec];
      
      // Save preferences after a short delay to avoid too many requests
      setTimeout(() => savePreferences(newSelection), 500);
      
      return newSelection;
    });
  };

  // Clear all specialisations
  const clearSpecialisations = () => {
    setSelectedSpecialisations([]);
    savePreferences([]);
  };

  useEffect(() => {
    const fetchCourses = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (level && level !== 'all') params.append('level', level);
        if (selectedSpecialisations.length > 0) {
          params.append('specialisations', selectedSpecialisations.join(','));
        }

        const token = localStorage.getItem('token');
        let res;
        
        if (token) {
          // Authenticated user - use authenticated endpoint
          res = await axios.get(`${API}/courses?${params.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          // Public user - use public endpoint
          res = await axios.get(`${API}/public/courses?${params.toString()}`);
        }
        
        let coursesData = res.data || [];

        // Client-side sorting
        switch (sortBy) {
          case 'price-low':
            coursesData.sort((a, b) => (a.price || 0) - (b.price || 0));
            break;
          case 'price-high':
            coursesData.sort((a, b) => (b.price || 0) - (a.price || 0));
            break;
          case 'cpd':
            coursesData.sort((a, b) => (b.cpd_points || 0) - (a.cpd_points || 0));
            break;
          case 'newest':
          default:
            coursesData.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        }

        setCourses(coursesData);
      } catch (error) {
        console.error('Error fetching courses:', error);
        setCourses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [search, level, sortBy, selectedSpecialisations]);

  // Fetch external courses (only if feature is enabled)
  useEffect(() => {
    if (!showExternalCourses) {
      setExternalCourses([]);
      return;
    }

    const fetchExternalCourses = async () => {
      try {
        const params = new URLSearchParams();
        params.append('limit', '200'); // Fetch all external courses
        if (selectedExternalSpec && selectedExternalSpec !== 'all') {
          params.append('specialisation', selectedExternalSpec);
        }
        
        const extToken = localStorage.getItem('token');
        const res = await axios.get(`${API}/public/external-courses?${params.toString()}`, { headers: extToken ? { Authorization: `Bearer ${extToken}` } : {} });
        let modules = [];
        if (selectedExternalSpec === 'all') {
          try {
            const token = localStorage.getItem('token');
            const modRes = await axios.get(`${API}/external-learning/modules`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            modules = (modRes.data || []).map(m => ({ ...m, specialisation: 'External Learning', provider: (m.provider || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) }));
          } catch (e) {
            modules = [];
          }
        }
        setExternalCourses([...modules, ...(res.data.courses || [])]);
        setExternalSpecialisations(res.data.specialisations || []);
      } catch (error) {
        console.error('Error fetching external courses:', error);
        setExternalCourses([]);
      }
    };

    fetchExternalCourses();
  }, [selectedExternalSpec, showExternalCourses]);

  const handleSearch = (e) => {
    e.preventDefault();
    // Update URL params
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (level && level !== 'all') params.set('level', level);
    if (sortBy) params.set('sort', sortBy);
    if (selectedSpecialisations.length > 0) {
      params.set('specialisations', selectedSpecialisations.join(','));
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearch('');
    setLevel('all');
    setSortBy('newest');
    setSelectedSpecialisations([]);
    setSearchParams({});
    savePreferences([]);
  };

  const hasActiveFilters = search || (level && level !== 'all') || selectedSpecialisations.length > 0;
  const activeFilterCount = [
    search, 
    level && level !== 'all',
    selectedSpecialisations.length > 0
  ].filter(Boolean).length;

  return (
    <Layout showBreadcrumb={false}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <Breadcrumb 
          items={[
            { label: 'Dashboard', href: '/dashboard', isRoot: true },
            { label: 'Course Catalog', isCurrent: true }
          ]} 
          className="mb-4"
        />
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-bold text-white mb-2">
            Course Catalog
          </h1>
          <p className="text-zinc-400">
            Explore our collection of professional courses and advance your career
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 space-y-4">
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search courses..."
                className="pl-12 bg-navigate-surface border-zinc-800 text-white placeholder:text-zinc-500 h-12"
                data-testid="course-search"
              />
            </div>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 h-12 rounded-lg border flex items-center gap-2 transition-colors ${
                showFilters || hasActiveFilters
                  ? 'bg-navigate-gold text-black border-navigate-gold'
                  : 'bg-navigate-surface border-zinc-800 text-white hover:border-zinc-700'
              }`}
              data-testid="filter-toggle"
            >
              <SlidersHorizontal className="h-5 w-5" />
              <span className="hidden sm:inline">Filters</span>
              {hasActiveFilters && (
                <span className="ml-1 w-5 h-5 rounded-full bg-black/20 text-xs flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </form>

          {/* Filter Panel */}
          {showFilters && (
            <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6 animate-fade-in relative z-10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-white">Filters</h3>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-navigate-gold hover:text-navigate-gold-light flex items-center gap-1"
                  >
                    <X className="h-4 w-4" />
                    Clear All
                  </button>
                )}
              </div>
              
              {/* Specialisation Multi-Select */}
              <div className="relative z-20">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-zinc-400">Specialisation</label>
                  {selectedSpecialisations.length > 0 && (
                    <button
                      onClick={clearSpecialisations}
                      className="text-xs text-navigate-gold hover:text-navigate-gold-light"
                    >
                      Clear ({selectedSpecialisations.length})
                    </button>
                  )}
                </div>
                
                {/* Custom Multi-Select Dropdown */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowSpecDropdown(!showSpecDropdown)}
                    className="w-full px-4 py-2.5 bg-navigate-dark border border-zinc-700 rounded-lg text-left flex items-center justify-between hover:border-zinc-600 transition-colors"
                    data-testid="specialisation-filter"
                  >
                    <span className={selectedSpecialisations.length > 0 ? 'text-white' : 'text-zinc-500'}>
                      {selectedSpecialisations.length === 0 
                        ? 'Select specialisations...'
                        : selectedSpecialisations.length === 1
                          ? selectedSpecialisations[0]
                          : `${selectedSpecialisations.length} selected`}
                    </span>
                    <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${showSpecDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showSpecDropdown && (
                    <div className="absolute z-[100] w-full mt-2 bg-navigate-surface border border-zinc-700 rounded-lg shadow-xl max-h-80 overflow-hidden">
                      {/* Select All / Clear All header */}
                      <div className="border-b border-zinc-700 px-4 py-2 flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedSpecialisations([...SPECIALISATIONS]);
                            savePreferences([...SPECIALISATIONS]);
                          }}
                          className="text-xs text-navigate-gold hover:text-navigate-gold-light"
                        >
                          Select All
                        </button>
                        {selectedSpecialisations.length > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedSpecialisations([]);
                              savePreferences([]);
                            }}
                            className="text-xs text-zinc-400 hover:text-white"
                          >
                            Clear All
                          </button>
                        )}
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {SPECIALISATIONS.map((spec) => (
                          <button
                            key={spec}
                            type="button"
                            onClick={() => toggleSpecialisation(spec)}
                            className={`w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-zinc-800 transition-colors ${
                              selectedSpecialisations.includes(spec) ? 'bg-zinc-800/50' : ''
                            }`}
                            data-testid={`spec-option-${spec.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                              selectedSpecialisations.includes(spec)
                                ? 'bg-navigate-gold border-navigate-gold'
                                : 'border-zinc-600'
                            }`}>
                              {selectedSpecialisations.includes(spec) && (
                                <Check className="h-3 w-3 text-black" />
                              )}
                            </div>
                            <span className="text-sm text-white">{spec}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Selected Tags Display */}
                {selectedSpecialisations.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {selectedSpecialisations.map((spec) => (
                      <span
                        key={spec}
                        className="inline-flex items-center gap-1.5 px-3 py-1 bg-navigate-gold/20 text-navigate-gold text-xs rounded-full"
                      >
                        {spec}
                        <button
                          onClick={() => toggleSpecialisation(spec)}
                          className="hover:text-white transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Level Filter */}
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">Level</label>
                  <Select value={level} onValueChange={setLevel}>
                    <SelectTrigger className="bg-navigate-dark border-zinc-700">
                      <SelectValue placeholder="All Levels" />
                    </SelectTrigger>
                    <SelectContent className="bg-navigate-surface border-zinc-800">
                      {LEVELS.map((lvl) => (
                        <SelectItem 
                          key={lvl.value} 
                          value={lvl.value}
                          className="hover:bg-zinc-800"
                        >
                          {lvl.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Sort */}
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">Sort By</label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="bg-navigate-dark border-zinc-700">
                      <SelectValue placeholder="Newest First" />
                    </SelectTrigger>
                    <SelectContent className="bg-navigate-surface border-zinc-800">
                      {SORT_OPTIONS.map((opt) => (
                        <SelectItem 
                          key={opt.value} 
                          value={opt.value}
                          className="hover:bg-zinc-800"
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
            </div>
          )}
        </div>

        {/* Click outside to close dropdown */}
        {showSpecDropdown && (
          <div 
            className="fixed inset-0 z-[90]" 
            onClick={() => setShowSpecDropdown(false)}
          />
        )}

        {/* Course Type Tabs - Only show if external courses feature is enabled */}
        {showExternalCourses && (
          <div className="flex gap-2 mb-6 border-b border-zinc-800">
            <button
              data-testid="catalog-tab-courses"
              onClick={() => setActiveTab('internal')}
              className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                activeTab === 'internal'
                  ? 'text-navigate-gold'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              Courses ({courses.length})
              {activeTab === 'internal' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-navigate-gold" />
              )}
            </button>
            <button
              data-testid="catalog-tab-external"
              onClick={() => setActiveTab('external')}
              className={`px-4 py-3 text-sm font-medium transition-colors relative flex items-center gap-2 ${
                activeTab === 'external'
                  ? 'text-navigate-gold'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              <Globe className="h-4 w-4" />
              External ({externalCourses.length})
              {activeTab === 'external' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-navigate-gold" />
              )}
            </button>
          </div>
        )}

        {/* Internal Courses Tab */}
        {activeTab === 'internal' && (
          <>
            {/* Results */}
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm text-zinc-400">
                {loading ? 'Loading courses...' : `${courses.length} course${courses.length !== 1 ? 's' : ''} found`}
              </p>
            </div>

            {/* Course Grid */}
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div key={i} className="bg-navigate-surface border border-zinc-800 rounded-xl overflow-hidden animate-pulse">
                    <div className="aspect-video bg-zinc-800" />
                    <div className="p-5 space-y-3">
                      <div className="h-4 bg-zinc-800 rounded w-1/3" />
                      <div className="h-5 bg-zinc-800 rounded" />
                      <div className="h-4 bg-zinc-800 rounded w-2/3" />
                      <div className="h-4 bg-zinc-800 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : courses.length === 0 ? (
              <div className="text-center py-16">
                <Search className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-white mb-2">No courses found</h3>
                <p className="text-zinc-400 mb-6">
                  {hasActiveFilters 
                    ? 'Try adjusting your filters or search terms'
                    : 'Check back soon for new courses'}
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="btn-outline"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {courses.map((course) => (
                  <CourseCard 
                    key={course.course_id} 
                    course={course} 
                    isInPathway={pathwayItemIds.has(course.course_id)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* External Courses Tab - Only show if feature is enabled */}
        {showExternalCourses && activeTab === 'external' && (
          <>
            {/* Quick Filter Chips */}
            <div className="mb-4">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedExternalSpec('all')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedExternalSpec === 'all'
                      ? 'bg-navigate-gold text-black'
                      : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700 border border-zinc-700'
                  }`}
                  data-testid="quick-filter-all"
                >
                  All Courses
                </button>
                {externalSpecialisations.map((spec) => (
                  <button
                    key={spec}
                    onClick={() => setSelectedExternalSpec(spec)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedExternalSpec === spec
                        ? 'bg-navigate-gold text-black'
                        : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700 border border-zinc-700'
                    }`}
                    data-testid={`quick-filter-${spec.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {spec}
                  </button>
                ))}
              </div>
            </div>

            {/* External Course Description & Dropdown Filter */}
            <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <p className="text-sm text-zinc-400">
                Browse external courses and third-party learning modules
              </p>
              <div className="sm:w-64">
                <Select value={selectedExternalSpec} onValueChange={setSelectedExternalSpec}>
                  <SelectTrigger className="bg-navigate-dark border-zinc-700">
                    <SelectValue placeholder="All Specialisations" />
                  </SelectTrigger>
                  <SelectContent className="bg-navigate-surface border-zinc-800 z-[100]">
                    <SelectItem value="all" className="hover:bg-zinc-800">All Specialisations</SelectItem>
                    {externalSpecialisations.map((spec) => (
                      <SelectItem 
                        key={spec} 
                        value={spec}
                        className="hover:bg-zinc-800"
                      >
                        {spec}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* External Results Count */}
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm text-zinc-400">
                {externalCourses.length} external course{externalCourses.length !== 1 ? 's' : ''} available
                {selectedExternalSpec !== 'all' && (
                  <span className="ml-2 text-navigate-gold">
                    in {selectedExternalSpec}
                  </span>
                )}
              </p>
              {selectedExternalSpec !== 'all' && (
                <button
                  onClick={() => setSelectedExternalSpec('all')}
                  className="text-sm text-zinc-400 hover:text-white flex items-center gap-1"
                >
                  <X className="h-3 w-3" />
                  Clear filter
                </button>
              )}
            </div>

            {/* External Course Grid */}
            {externalCourses.length === 0 ? (
              <div className="text-center py-16">
                <svg className="h-12 w-12 text-zinc-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                <h3 className="text-xl font-medium text-white mb-2">No courses found</h3>
                <p className="text-zinc-400 mb-6">
                  {selectedExternalSpec !== 'all' 
                    ? `No courses available in "${selectedExternalSpec}"`
                    : 'External courses will appear here once configured'}
                </p>
                {selectedExternalSpec !== 'all' && (
                  <Button
                    onClick={() => setSelectedExternalSpec('all')}
                    variant="outline"
                    className="border-zinc-700"
                  >
                    Show All Courses
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {externalCourses.map((course) => (
                  <ExternalCourseCard key={course.course_id || course.module_id} course={course} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
